import { GraduationCap, Users, Receipt, Award, Sparkles, Zap, Users as UsersIcon, Wallet, Clock, Lock } from 'lucide-react';

interface ValuePointsProps {
  points: string[];
}

// Statistical cards data (use provided homepage SVGs for these four tiles)
const stats = [
  {
    // designer SVG for savings / students
    svg: '/images/homepage/savings.svg',
    number: '500+',
    description: 'Students & counting',
  },
  {
    // designer SVG for people / schools
    svg: '/images/homepage/ideas-person.svg',
    number: '100+',
    description: 'Schools & counting',
  },
  {
    // designer SVG for analytics / transactions
    svg: '/images/homepage/analytics.svg',
    number: '50K+',
    description: 'Transactions handled',
  },
  {
    // designer SVG for notepad / certificates
    svg: '/images/homepage/notepad-svg.svg',
    number: '1K+',
    description: 'Certificates issued',
  },
];

// Icon mapping for value points
const valueIcons = [
  Sparkles, // Less admin. More teaching.
  Zap,      // Clarity at a glance.
  UsersIcon, // Parents in the loop.
  Wallet,   // Money handled right.
  Clock,    // Fast. Familiar. Forgiving.
  Lock,     // Yours, not ours.
];

export function ValuePoints({ points }: ValuePointsProps) {
  // Use all 6 points for the feature list
  const features = points;

  return (
    <div className="px-3 xs:px-4 sm:px-6 md:px-8 py-6 xs:py-6 sm:py-8 md:py-10 lg:py-12 mx-2 sm:mx-4 lg:mx-8">
      <div className="max-w-7xl mx-auto">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xs:gap-6 md:gap-8 lg:gap-10 items-start lg:items-center">
          {/* Left Side - Statistical Cards */}
          <div className="grid grid-cols-2 gap-3 xs:gap-3 w-full max-w-md mx-auto mb-4 xs:mb-6 sm:mb-8">
            {/* Left column (1st and 3rd) */}
            <div className="space-y-3">
              {[stats[0], stats[2]].map((stat, index) => {
                const IconComponent = (stat as any).icon as any;
                return (
                  <div
                    key={`left-${index}`}
                    className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-300 flex items-center justify-center aspect-square"
                  >
                    <div className="flex flex-col items-center text-center gap-3">
                      {/* Icon */}
                      {stat.svg ? (
                        <img
                          src={stat.svg}
                          alt={stat.description}
                          className="w-auto h-auto max-w-[50%] max-h-[50%] xs:max-w-[55%] xs:max-h-[55%] sm:max-w-[60%] sm:max-h-[60%] object-contain"
                        />
                      ) : (
                        IconComponent ? (
                          <IconComponent className="w-auto h-auto max-w-[50%] max-h-[50%] xs:max-w-[55%] xs:max-h-[55%] sm:max-w-[60%] sm:max-h-[60%] text-[#0D373D]" />
                        ) : null
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right column (2nd and 4th) - vertically offset for staggered layout */}
            <div className="space-y-3 mt-2 xs:mt-3 sm:mt-4">
              {[stats[1], stats[3]].map((stat, index) => {
                const IconComponent = (stat as any).icon as any;
                return (
                  <div
                    key={`right-${index}`}
                    className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-300 flex items-center justify-center aspect-square"
                  >
                    <div className="flex flex-col items-center text-center gap-3">
                      {/* Icon */}
                      {stat.svg ? (
                        <img
                          src={stat.svg}
                          alt={stat.description}
                          className="w-auto h-auto max-w-[50%] max-h-[50%] xs:max-w-[55%] xs:max-h-[55%] sm:max-w-[60%] sm:max-h-[60%] object-contain"
                        />
                      ) : (
                        IconComponent ? (
                          <IconComponent className="w-auto h-auto max-w-[50%] max-h-[50%] xs:max-w-[55%] xs:max-h-[55%] sm:max-w-[60%] sm:max-h-[60%] text-[#0D373D]" />
                        ) : null
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="space-y-4 xs:space-y-5 sm:space-y-6">
            {/* Main Heading */}
            <h2 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-[#1D2125] leading-tight">
              Value at Glance
            </h2>

            {/* Descriptive Subtext */}
            <p className="text-sm xs:text-base sm:text-lg text-[#4D4F52] leading-relaxed">
               Nest by Emmple brings together all core administrative tasks—attendance, fees, grades, transport, and more—into one seamless system. Fast for teachers. Clear for leadership. Effortless for parents.
            </p>

            {/* Feature List - Vertical Stack */}
            <div className="space-y-2 xs:space-y-3 pt-1 xs:pt-2 sm:pt-3">
              {features.map((feature, index) => {
                const IconComponent = valueIcons[index] || Sparkles;
                return (
                  <div key={index} className="flex items-center gap-3 xs:gap-4">
                    {/* Icon in Circular Container with Secondary Color Background */}
                    <div className="flex-shrink-0 w-6 xs:w-7 sm:w-8 h-6 xs:h-7 sm:h-8 rounded-full bg-[#E2FDCB] flex items-center justify-center">
                      <IconComponent className="h-3 xs:h-3.5 sm:h-4 w-3 xs:w-3.5 sm:w-4 text-[#0D373D]" strokeWidth={2} />
                    </div>
                    {/* Feature Text */}
                    <p className="text-sm xs:text-base sm:text-lg font-medium text-[#1D2125]">
                      {feature}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
